// ONBW1-S1 (docs/build-briefs/ONBW1-S1-welcome-link.md; DEC-416; design/mockups/onboarding-wizard/b0-1-welcome and
// b0-3-pin). The screens a new owner sees on the device after opening the welcome link:
//   1. The link opens: the device is bound to the location (the server redeems the code issued with the link), and the
//      screen says Welcome, <name>, with the business, the location and this device.
//   2. Choose your PIN: a touch pad, by the location's own PIN policy, and Save PIN.
//   3. The server signs the owner in and the page carries on exactly as after a PIN sign-in, to the setup wizard.
// The keypad code is the fallback: a device bound by a code issued with a link lands on the same PIN choice.
//
// The token reaches this page in the address fragment (main.ts reads it once and removes it). It is sent in a request
// body, never put in a query string and never logged. The PIN lives in this closure while it is typed, goes to the
// server once, and is cleared; it is never drawn (only dots) and never logged. Every text is set with textContent.

import {
  chooseWelcomePin,
  getWelcomePending,
  redeemWelcomeLink,
  type SignInBody,
  type WelcomePendingDto,
} from "./api.ts";
import { generateDeviceSecret } from "./deviceCredential.ts";
import { terminalConfigFromRedemption, type TerminalConfig } from "./session.ts";
import {
  appendPinDigit,
  canSavePin,
  clearAttempt,
  pendingAttemptToken,
  secretForAttempt,
  deletePinDigit,
  describePinFailure,
  pinDots,
  pinHintText,
  pinNoteText,
  welcomeHeading,
  WELCOME_CONNECTED_TEXT,
} from "./welcomeLinkModel.ts";

export interface WelcomeFlowDeps {
  /** The server address to start from: the page's own origin. */
  apiBaseUrl: () => string;
  /** The config now in force, if any. */
  currentConfig: () => TerminalConfig | undefined;
  /** The device is bound: save the config and keep it as the terminal's own. */
  onConnected: (config: TerminalConfig) => void;
  /** The owner chose a PIN and is signed in: finish exactly as a PIN sign-in does. */
  onSignedIn: (config: TerminalConfig, body: SignInBody) => Promise<void>;
  /** Makes the welcome screen the one on show. */
  showScreen: () => void;
  /** The link did not work: the code keypad is the way in. */
  onUseCode: () => void;
  /** The device is bound and no PIN is waiting: on to the ordinary sign-in. */
  onContinue: () => void;
}

export interface WelcomeFlow {
  /** The page was opened with a welcome link. */
  openLink: (token: string) => Promise<void>;
  /** The token of an open attempt that never got an answer (a lost response), so a reload can resume it. */
  pendingToken: () => string | undefined;
  /** A device bound by a code issued with a welcome link has the PIN still to choose. True when it showed it. */
  resumeIfPending: (config: TerminalConfig) => Promise<boolean>;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  value?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (value !== undefined) node.textContent = value;
  return node;
}

function tid<T extends HTMLElement>(node: T, testid: string): T {
  node.dataset["testid"] = testid;
  return node;
}

function actionButton(label: string, className: string, testid: string): HTMLButtonElement {
  const node = el("button", className, label);
  node.type = "button";
  return tid(node, testid);
}

export function mountWelcome(root: HTMLElement, deps: WelcomeFlowDeps): WelcomeFlow {
  function frame(): { body: HTMLElement; note: HTMLElement; foot: HTMLElement } {
    const shell = el("div", "welcome-shell");
    const top = el("div", "welcome-top");
    const brand = el("div", "welcome-brand", "Snip & Sip");
    brand.append(el("small", "", "Set up your location"));
    top.append(brand);
    const body = el("div", "welcome-body");
    const foot = el("div", "welcome-foot");
    const note = el("div", "welcome-note");
    foot.append(note);
    shell.append(top, body, foot);
    root.replaceChildren(shell);
    deps.showScreen();
    return { body, note, foot };
  }

  function showRefused(retryToken: string | undefined): void {
    const { body, foot } = frame();
    const col = el("div", "welcome-col");
    col.append(tid(el("h1", "welcome-heading", "That link did not work"), "welcome-heading"));
    body.append(col);
    if (retryToken !== undefined) {
      const retry = actionButton("Try again", "welcome-secondary", "welcome-try-again");
      retry.addEventListener("click", () => void openLink(retryToken));
      foot.append(retry);
    }
    const useCode = actionButton("Enter a code", "welcome-primary", "welcome-enter-code");
    useCode.addEventListener("click", () => deps.onUseCode());
    foot.append(useCode);
  }

  /** b0-1: Welcome, <name>. */
  function showWelcome(config: TerminalConfig, pending: WelcomePendingDto): void {
    const { body, foot } = frame();
    const col = el("div", "welcome-col");
    col.append(
      tid(el("h1", "welcome-heading", welcomeHeading(pending.ownerName)), "welcome-heading"),
    );
    col.append(el("p", "welcome-hint", `This device is now set up for ${pending.locationName}.`));
    const stack = el("div", "welcome-stack");
    const card = el("div", "welcome-card");
    const rows: ReadonlyArray<readonly [string, string, string]> = [
      ["Business", pending.businessName, "welcome-business"],
      ["Location", pending.locationName, "welcome-location"],
      ["This device", pending.deviceName, "welcome-device"],
    ];
    for (const [label, value, testid] of rows) {
      const row = el("div", "welcome-sum");
      row.append(el("span", "", label), tid(el("b", "", value), testid));
      card.append(row);
    }
    const banner = el("div", "welcome-banner");
    banner.append(el("span", "welcome-check", "✓"), el("span", "", WELCOME_CONNECTED_TEXT));
    stack.append(card, banner);
    col.append(stack);
    body.append(col);
    const choose = actionButton("Choose your PIN", "welcome-primary", "welcome-choose-pin");
    choose.addEventListener("click", () => showPin(config, pending));
    foot.append(choose);
  }

  /** b0-3: Choose your PIN. */
  function showPin(config: TerminalConfig, pending: WelcomePendingDto): void {
    const { body, note, foot } = frame();
    let digits = "";
    let saving = false;
    const { pinMinLength: min, pinMaxLength: max } = pending;
    const col = el("div", "welcome-col");
    col.append(tid(el("h1", "welcome-heading", "Choose your PIN"), "welcome-heading"));
    col.append(el("p", "welcome-hint", pinHintText(min, max)));
    const stack = el("div", "welcome-stack welcome-stack-pin");
    const dots = tid(el("div", "welcome-dots"), "welcome-pin-dots");
    const pad = el("div", "welcome-pad");
    const error = tid(el("p", "welcome-error", ""), "welcome-pin-error");
    error.setAttribute("role", "alert");
    const save = actionButton("Save PIN", "welcome-primary", "welcome-save-pin");

    const sync = (): void => {
      dots.replaceChildren(
        ...pinDots(digits, max).map((filled) => el("i", filled ? "filled" : "")),
      );
      dots.dataset["filled"] = String(digits.length);
      save.disabled = saving || !canSavePin(digits, min);
      save.classList.toggle("off", save.disabled);
    };
    const keys: ReadonlyArray<string> = [
      "1",
      "2",
      "3",
      "4",
      "5",
      "6",
      "7",
      "8",
      "9",
      "",
      "0",
      "Delete",
    ];
    for (const key of keys) {
      if (key === "") {
        pad.append(el("span", "welcome-key-gap"));
        continue;
      }
      const isDelete = key === "Delete";
      const keyButton = actionButton(
        key,
        isDelete ? "welcome-key welcome-key-fn" : "welcome-key",
        isDelete ? "welcome-key-delete" : `welcome-key-${key}`,
      );
      keyButton.addEventListener("click", () => {
        if (saving) return;
        error.textContent = "";
        digits = isDelete ? deletePinDigit(digits) : appendPinDigit(digits, key, max);
        sync();
      });
      pad.append(keyButton);
    }
    stack.append(dots, pad, error);
    col.append(stack);
    body.append(col);
    note.textContent = pinNoteText(min, max);
    save.addEventListener("click", () => {
      if (saving || !canSavePin(digits, min)) return;
      saving = true;
      sync();
      const chosen = digits;
      void chooseWelcomePin(config.apiBaseUrl, config.tenantId, config.deviceToken, chosen).then(
        async (result) => {
          if (result.status === 200 && result.body.token !== undefined) {
            digits = "";
            await deps.onSignedIn(config, result.body as SignInBody);
            return;
          }
          saving = false;
          digits = "";
          error.textContent = describePinFailure(result.status, result.body.message, min, max);
          sync();
        },
      );
    });
    foot.append(save);
    sync();
  }

  async function showPendingFor(config: TerminalConfig): Promise<boolean> {
    const pending = await getWelcomePending(config.apiBaseUrl, config.tenantId, config.deviceToken);
    if (pending.status !== 200 || pending.body.welcome === undefined) return false;
    showWelcome(config, pending.body.welcome);
    return true;
  }

  async function openLink(token: string): Promise<void> {
    const apiBaseUrl = deps.apiBaseUrl();
    // The same secret on every try of this attempt, so a response that was lost replays cleanly instead of being
    // refused as a different device.
    const secret = secretForAttempt(window.sessionStorage, token, generateDeviceSecret);
    const result = await redeemWelcomeLink(apiBaseUrl, { token, deviceSecret: secret });
    if (result.networkError) {
      showRefused(token);
      return;
    }
    // The server has answered, whatever it said: the attempt is over.
    clearAttempt(window.sessionStorage);
    const redeemed = result.body as Partial<{
      deviceId: string;
      tenantId: string;
      locationId: string;
      locationName: string;
      displayName: string;
      businessName: string;
      welcome: boolean;
    }>;
    if (
      (result.status !== 200 && result.status !== 201) ||
      redeemed.deviceId === undefined ||
      redeemed.tenantId === undefined ||
      redeemed.locationId === undefined ||
      redeemed.locationName === undefined ||
      redeemed.displayName === undefined ||
      redeemed.businessName === undefined
    ) {
      showRefused(undefined);
      return;
    }
    const config = terminalConfigFromRedemption(
      apiBaseUrl,
      {
        deviceId: redeemed.deviceId,
        tenantId: redeemed.tenantId,
        locationId: redeemed.locationId,
        locationName: redeemed.locationName,
        displayName: redeemed.displayName,
        businessName: redeemed.businessName,
      },
      secret,
      deps.currentConfig(),
    );
    deps.onConnected(config);
    // Bound, but nothing is waiting for a PIN (the owner already has one): the ordinary sign-in is next.
    if (!(await showPendingFor(config))) deps.onContinue();
  }

  async function resumeIfPending(config: TerminalConfig): Promise<boolean> {
    return showPendingFor(config);
  }

  return {
    openLink,
    resumeIfPending,
    pendingToken: () => pendingAttemptToken(window.sessionStorage),
  };
}
